/**
 * Permission map for the Artikel detail page (`/lager/artikel/:id`).
 *
 * Strategy:
 * - Page: hide entire route content without view permission.
 * - Tab: hide tab when no subsection permission applies.
 * - Field: hide control or read-only fallback from `artikel/read_one` (1001).
 * - API: never call endpoints without the matching read permission.
 * - Toast: only on explicit user actions (save / delete / refresh), not background loads.
 */

export interface MaterialDetailsPermissionEntry {
    id: number;
    label: string;
    api?: string;
    uiPattern: 'page' | 'tab' | 'section' | 'field' | 'action';
}

export const MATERIAL_DETAILS_PERMISSIONS = {
  page: {
    view: { id: 1001, label: 'Lager - Artikel - anzeigen', api: 'artikel/read_one.php', uiPattern: 'page' },
    edit: { id: 1003, label: 'Lager - Artikel - editieren', api: 'artikel/update_*.php', uiPattern: 'action' },
    mandanten: {
      id: 1005,
      label: 'Lager - Artikel - mögl. Mandanten anzeigen',
      api: 'artikel/read_mandanten.php',
      uiPattern: 'action',
    },
    untersuchungSave: {
      id: 1007,
      label: 'Lager - Artikel - Untersuchung speichern',
      api: 'artikel/update_untersuchung.php',
      uiPattern: 'action',
    },
  },
  tabs: {
    allgemein: {
      label: 'Allgemein',
      tabPermission: 1001,
      note: 'Always shown when page is accessible (creation uses same tab).',
    },
    preise: {
      label: 'Preise',
      tabPermission: [3201, 3211, 3411, 1091, 1099, 1000, 1010],
      note: 'Tab visible if user has any price-related permission.',
    },
    einstellungen: {
      label: 'Einstellungen',
      tabPermission: 3203,
      note: 'Toggles auto-save via update_einstellungen; requires 3203.',
    },
    vet: {
      label: 'VET',
      tabPermission: 1001,
      note: 'Values from artikel/read_one; save requires 1003.',
    },
    medikation: {
      label: 'Medikation',
      tabPermission: [1291, 1301, 1691, 1891],
      note: 'Tab visible if any medikation subsection permission exists.',
    },
    untersuchung: {
      label: 'Untersuchung',
      tabPermission: [1007],
      note: 'Tab and save require 1007 (Lager - Artikel - Untersuchung speichern).',
    },
    stueckliste: {
      label: 'Stückliste',
      tabPermission: 4401,
      note: 'Only when artikel.stueckliste === 1.',
    },
  },
  fields: {
    wirkstoff: {
      view: { id: 4451, label: 'Lager - Wirkstoffe - anzeigen', api: 'wirkstoff/read.php', uiPattern: 'field' },
      fallback: 'Editable text from artikel/read_one (1001); no wirkstoff/read.php lookup without 4451',
    },
    einheit: {
      view: { id: 1791, label: 'Lager - Einheit - anzeigen', api: 'einheit/read.php', uiPattern: 'field' },
      fallback: 'material.einheit from artikel/read_one (1001)',
    },
    warengruppe: {
      view: { id: 2571, label: 'Lager - Warengruppe - anzeigen', api: 'warengruppe/read.php', uiPattern: 'field' },
      fallback: 'material.warengruppe from artikel/read_one (1001)',
    },
    labor: {
      view: { id: 1071, label: 'Lager - Lieferanten - anzeigen', api: 'lieferant/read.php', uiPattern: 'field' },
      fallback: 'material.lieferant_labor from artikel/read_one (1001)',
    },
    untersuchungsgruppe: {
      view: {
        id: 1441,
        label: 'Labor - Untersuchungsgruppe - anzeigen',
        api: 'untersuchungsgruppe/read.php',
        uiPattern: 'field',
      },
      fallback: 'material.untersuchungsgruppe from artikel/read_one (1001)',
    },
    tskPreis: {
      view: { id: 1612, label: 'TSK Preis field', uiPattern: 'field' },
    },
  },
  preise: {
    verkaufspreise: {
      view: { id: 3201, label: 'Administration - Artikel Verkaufspreise - anzeigen', api: 'artikel_verkaufspreis/read_vk.php' },
      handelsspanne: { id: 3205, label: 'Administration - Artikel Verkaufspreise - Handelsspanne anzeigen' },
    },
    finanzen: {
      view: { id: 3211, label: 'Administration - Artikel Admin - anzeigen', api: 'artikel_admin/read_one.php' },
    },
    lieferanten: {
      view: { id: 1091, label: 'Artikel-Lieferant Zuordnung', api: 'artikel_lieferant/read.php' },
    },
    stuecklistenEk: {
      view: { id: 1099, label: 'Stücklisten-Einkaufspreis Anzeige' },
    },
    rabattstaffel: {
      view: { id: 3411, label: 'Administration - Artikel Rabattstaffeln - anzeigen', api: 'artikel_rabattstaffel/read.php' },
    },
    historie: {
      view: { id: 1000, label: 'Verkaufspreis-Historie' },
      lieferantHistorie: { id: 1010, label: 'Lieferanten-Historie' },
    },
  },
  medikation: {
    indikationen: {
      view: { id: 1291, label: 'Belege - Indikationen - anzeigen', api: 'indikation/read_grouped.php' },
      edit: { id: 1293, label: 'Belege - Indikationen - editieren' },
      delete: { id: 1294, label: 'Belege - Indikationen - löschen' },
    },
    tierarten: {
      view: { id: 1301, label: 'Artikel Tierart anzeigen', api: 'artikel_tierart/read.php' },
      edit: { id: 1303, label: 'Artikel Tierart editieren' },
      delete: { id: 1304, label: 'Artikel Tierart löschen' },
    },
    anwendungen: {
      view: { id: 1691, label: 'Belege - Anwendungen - anzeigen', api: 'anwendung/read_grouped.php' },
      edit: { id: 1693, label: 'Belege - Anwendungen - editieren' },
      delete: { id: 1694, label: 'Belege - Anwendungen - löschen' },
    },
    sonderfaelle: {
      view: { id: 1891, label: 'Lager - Artikel Medikation Sonderfälle - anzeigen', api: 'artikel_medikation/read.php' },
      edit: { id: 1892, label: 'Lager - Artikel Medikation Sonderfälle - editieren' },
      delete: { id: 1894, label: 'Lager - Artikel Medikation Sonderfälle - löschen' },
    },
  },
  stueckliste: {
    view: { id: 4401, label: 'Lager - Artikel Stücklisten - anzeigen' },
    create: { id: 4402, label: 'Lager - Artikel Stücklisten - anlegen' },
    edit: { id: 4403, label: 'Lager - Artikel Stücklisten - editieren' },
    delete: { id: 4404, label: 'Lager - Artikel Stücklisten - löschen' },
    preise: { id: 3201, label: 'Stücklisten-Preisspalten' },
  },
} as const;

/** Tab visibility: user needs at least one permission from the list. */
export const MATERIAL_DETAILS_TAB_PERMISSIONS = {
  preise: [3201, 3211, 3411, 1091, 1099, 1000, 1010],
  einstellungen: [3203],
  medikation: [1291, 1301, 1691, 1891],
  untersuchung: [1007],
  stueckliste: [4401],
} as const;
